// ============================================
// Tipos compartidos
// ============================================

export type Language = "typescript" | "javascript" | "sql";

export interface SqlTest {
  name: string;
  expectedRows: Array<Record<string, unknown>>;
  ignoreOrder?: boolean;
}

/** Lo que mantenemos en el form de admin (camelCase) */
export interface Exercise {
  id: string;
  title: string;
  language: Language;
  duration_min: number;
  description: string;     // visible en el modal de instrucciones al entrar al test
  docs: string;            // markdown del panel lateral "Docs" (referencias, ayudas)
  starter_code: string;
  reference_code: string;  // solución del autor, para validar tests en admin
  tests_code: string;       // JS code para TS/JS
  setup_sql: string;        // SQL setup para SQL
  sql_tests: SqlTest[];     // tests para SQL
  company_id: string | null; // null = template global legacy
  is_active: boolean;      // false = pausado (no aparece en /generate)
  created_at?: string;
  updated_at?: string;
}

export interface Assignment {
  id: string;
  candidate_name: string;
  candidate_email: string;
  exercise_ids: string[];
  created_at: string;
  expires_at: string | null;          // si llega null = sin vencimiento
  sent_email_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  invalidated_at: string | null;
  invalidation_reason: string | null;
  timed_out_at: string | null;
  violation_count: number;            // 1ª = warning, 2ª = invalidate
  violations: Violation[];            // historial completo para el evaluador
  search_id: string | null;
  company_id: string | null;
  current_exercise_index: number;             // 0-based, ejercicio actual del candidato
  current_exercise_started_at: string | null; // base del countdown del exercise current
}

// ============================================
// Empresas y búsquedas
// ============================================

export interface Company {
  id: string;
  name: string;
  razon_social: string | null;
  email: string | null;
  phone: string | null;
  rubro: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type Experience =
  | "junior"
  | "semi_senior"
  | "senior"
  | "tech_lead"
  | "manager"
  | "cto"
  | "ceo"
  | "other";

export const EXPERIENCE_LABELS: Record<Experience, string> = {
  junior: "Junior",
  semi_senior: "Semi-Senior",
  senior: "Senior",
  tech_lead: "Tech Lead",
  manager: "Manager",
  cto: "CTO",
  ceo: "CEO",
  other: "Otro",
};

// ============================================
// Postulaciones públicas (link de apply)
// ============================================

export type ApplicationStatus =
  | "new"
  | "reviewing"
  | "test_sent"
  | "rejected"
  | "hired";

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  new: "Nuevo",
  reviewing: "En revisión",
  test_sent: "Test enviado",
  rejected: "Descartado",
  hired: "Contratado",
};

export interface Application {
  id: string;
  search_id: string;
  company_id: string;
  candidate_name: string;
  candidate_email: string;
  linkedin_url: string | null;
  cv_url: string | null;
  cover_letter: string | null;
  status: ApplicationStatus;
  notes: string | null;
  test_assignment_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TeamMember {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  is_admin: boolean;
  company_ids: string[];
}

export interface Search {
  id: string;
  company_id: string;
  puesto: string;
  experience: Experience;
  rubro: string | null;
  description: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;            // "ARS" | "USD" | etc.
  technologies: string[];
  stack: string | null;
  is_open: boolean;
  created_at: string;
  updated_at: string;
}

export interface Violation {
  kind: "tab_switch" | "window_blur";
  at: string; // ISO timestamp
}

export interface TestResult {
  name: string;
  passed: boolean;
  error?: string;
}

export interface SubmissionRow {
  id?: string;
  assignment_id: string;
  exercise_id: string;
  attempt_n: number;
  code: string;
  test_results: TestResult[];
  passed_count: number;
  total_count: number;
  score: number;
  is_final: boolean;
  created_at?: string;
}
